import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import NavBar from './components/NavBar';
import UnsentResultBanner from './components/UnsentResultBanner';
import Footer from './components/Footer';
import AppRoutes from './components/AppRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import GameModals from './components/GameModals';
import { ConsentBanner } from './components/consent';
import { useGameState } from './hooks/useGameState';
import { useGameLogic } from './hooks/useGameLogic';
import { useUndoGame } from './hooks/useUndoGame';
import { useAudioContext } from './hooks/useAudioContext';
import { useBotAutoTurn } from './hooks/useBotAutoTurn';
import { useConsent } from './hooks/useConsent';
import { useSettings } from './contexts/SettingsContext';
import Cover from './media/TripleFreezerCover2.jpg';

// Routes that show the game board — stay narrow with the green felt gradient.
// Everything else (profile, tournaments, admin, events …) gets the wider layout.
const GAME_ROUTES = ['/lobby', '/game'];

/**
 * Full-viewport themed backdrop behind the app column. Fills wide monitors so
 * the centered column reads as an intentional stage instead of a phone app
 * floating on a blank page. Game routes get a dark "table" feel; content
 * routes get the brand cover art, dimmed to ambient level so the brighter
 * page-level cover images still read as the lit content area.
 */
const Backdrop = styled.div<{ $game: boolean }>`
  position: fixed;
  inset: 0;
  z-index: -1;
  background: ${({ $game }) =>
    $game
      ? `radial-gradient(60% 50% at 50% 42%, rgba(0, 118, 100, 0.45) 0%, rgba(0, 30, 35, 0) 72%),
         linear-gradient(180deg, #001f2c 0%, #000a12 100%)`
      : `linear-gradient(180deg, rgba(0, 13, 26, 0.92) 0%, rgba(0, 8, 16, 0.96) 100%),
         url(${Cover}) center / cover no-repeat #00111a`};
`;

const AppContainer = styled.div<{ $wide: boolean }>`
  max-width: ${({ $wide }) => ($wide ? '960px' : '600px')};
  margin: 0 auto;
  overflow: hidden;
  min-height: 100vh;
  /* Green felt gradient only on the game board */
  ${({ $wide }) =>
    !$wide &&
    `
    box-shadow: inset 0px 4px 4px 0px rgba(255, 255, 255, 0.25);
    background: radial-gradient(50% 50% at 50% 50%, rgba(0, 127, 127, 1), rgba(0, 64, 43, 1) 100%);
  `}

  /* Desktop: soft outer shadow frames the column against the backdrop */
  @media (min-width: 1024px) {
    box-shadow: ${({ $wide }) =>
      $wide
        ? '0 0 70px rgba(0, 0, 0, 0.6)'
        : 'inset 0px 4px 4px 0px rgba(255, 255, 255, 0.25), 0 0 70px rgba(0, 0, 0, 0.65)'};
  }

  /* Widescreen: let content routes breathe a little more */
  @media (min-width: 1440px) {
    max-width: ${({ $wide }) => ($wide ? '1120px' : '600px')};
  }
`;

const App: React.FC = () => {
  const { pathname } = useLocation();
  const isWide = !GAME_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));

  const [specialMessage, setSpecialMessage] = useState<string | null>(null);
  const [specialMessageDuration, setSpecialMessageDuration] = useState<number>(2500);
  // Sound preference now lives in the persisted settings store (managed on /settings)
  const { settings } = useSettings();
  const soundEnabled = settings.soundEnabled;

  // Consent management
  const {
    isConsentBannerVisible,
    handleConsentChange,
  } = useConsent();

  // Game state management
  const {
    players,
    setPlayers,
    currentPlayerIndex,
    setCurrentPlayerIndex,
    gameWon,
    setGameWon,
    winnerName,
    setWinnerName,
    clearGameState,
    saveGameStateToLocalStorage,
    gameState,
    setGameState,
    showMatchStats,
    setShowMatchStats,
    startNewMatch,
    completeMatch,
    isAllMatchesComplete,
    getFinalWinner,
    getTieInfo,
  } = useGameState();

  // Special message handling
  const handleSpecialMessage = (message: string) => {
    setSpecialMessage(message);
    
    // Set different durations for different message types
    if (message === 'Miss!') {
      setSpecialMessageDuration(1000); // 1.5 seconds for Miss
    } else {
      setSpecialMessageDuration(2500); // Default 2.5 seconds for other messages
    }
  };

  const dismissSpecialMessage = () => {
    setSpecialMessage(null);
    // Don't call proceedWithEndTurn() here anymore since it's now called directly after handleSpecialMessage
    saveGameStateToLocalStorage();
  };

  // Game logic
  const gameLogic = useGameLogic(
    players,
    setPlayers,
    currentPlayerIndex,
    setCurrentPlayerIndex,
    setGameWon,
    setWinnerName,
    handleSpecialMessage,
    saveGameStateToLocalStorage,
    clearGameState,
    soundEnabled,
    gameState,
    setGameState,
    completeMatch,
    setShowMatchStats
  );

  // Bot auto-turn integration
  // TODO: Pass actual botDifficulty from game options or state if available
  let botDifficulty: 'easy' | 'medium' | 'hard' = 'easy';
  // Try to infer botDifficulty from the players array if possible
  if (players.some((p) => p.id === 'bot') && gameLogic.isBotGame && gameLogic.isBotGame()) {
    // Try to get from gameLogic (if available)
    if (gameLogic.botGameRef && gameLogic.botGameRef.current && gameLogic.botGameRef.current.botDifficulty) {
      botDifficulty = gameLogic.botGameRef.current.botDifficulty;
    }
  }
  useBotAutoTurn({
    players,
    currentPlayerIndex,
    setPlayers,
    endTurn: gameLogic.endTurn,
    isBotGame: gameLogic.isBotGame,
    isBotTurn: gameLogic.isBotTurn,
    generateBotDice: gameLogic.generateBotDice,
    botDifficulty,
    gameWon,
  });

  // Undo functionality
  const undoGame = useUndoGame(
    players,
    setPlayers,
    currentPlayerIndex,
    setCurrentPlayerIndex,
    saveGameStateToLocalStorage
  );

  // Audio context management
  useAudioContext();

  return (
    <>
      <ConsentBanner
        isVisible={isConsentBannerVisible}
        onConsentChange={handleConsentChange}
      />
      <NavBar players={players} />
      {/* App-wide: an unsent tournament result must keep retrying after the
          player leaves the winner screen or reloads. */}
      <UnsentResultBanner />
      <Backdrop $game={!isWide} />
      {/* Semantic main landmark for screen readers */}
      <AppContainer as="main" $wide={isWide}>
        <GameModals
          specialMessage={specialMessage}
          onDismissSpecialMessage={dismissSpecialMessage}
          showUndoModal={undoGame.showUndoModal}
          onConfirmUndo={undoGame.confirmUndo}
          onCancelUndo={undoGame.cancelUndo}
          showQuitModal={gameLogic.showQuitModal || false}
          onConfirmQuit={gameLogic.confirmQuit || (() => {})}
          onCancelQuit={gameLogic.cancelQuit || (() => {})}
          specialMessageDuration={specialMessageDuration}
        />

        <ErrorBoundary>
        <AppRoutes
          players={players}
          setPlayers={setPlayers}
          gameWon={gameWon}
          winnerName={winnerName}
          onStartGame={gameLogic.startGame}
          clearGameState={clearGameState}
          onDiceValueChange={gameLogic.handleDiceValueChange}
          endTurn={gameLogic.endTurn}
          toggleFreeze={gameLogic.toggleFreeze}
          soundEnabled={soundEnabled}
          onUndo={undoGame.handleUndoClick}
          onRestart={gameLogic.handleRestartGame}
          onQuit={gameLogic.handleQuitGame}
          canUndo={undoGame.canUndo}
          gameState={gameState}
          setGameState={setGameState}
          showMatchStats={showMatchStats}
          setShowMatchStats={setShowMatchStats}
          startNewMatch={startNewMatch}
          completeMatch={completeMatch}
          isAllMatchesComplete={isAllMatchesComplete}
          getFinalWinner={getFinalWinner}
          getTieInfo={getTieInfo}
          botDifficulty={botDifficulty}
        />
        </ErrorBoundary>
      </AppContainer>
      <Footer />
    </>
  );
};

export default App;